import { useResourcesStore } from "@/store/resource"
import { useUsersStore } from "@/store/user"
import { createUserGroup, deleteUserGroup, getUserGroupById, getUserGroupNotifications, getUserGroups, saveUserGroupNotifications, updateUserGroup } from "../api/userGroup"
import { ResourceType } from "../types/resourceType"
import { User } from "../types/user"
import { UserGroup, UserGroupDTO, UserGroupNotification, UserGroupNotificationDTO } from "../types/userGroup"

export async function fetchUserGroupPagedData(params: PagedRequestParams): Promise<PagedResult<UserGroup>> {
    const res = await getUserGroups(params)

    const usersStore = useUsersStore()
    const userIds = res.items.flatMap(ug => ug.users)
    await usersStore.fetchByIds(userIds)

    const usersMap: Record<string, User> = {}
    usersStore.users.forEach(u => {
        usersMap[u.id] = u
    })

    const userGroups: UserGroup[] = res.items.map(dto => dtoToUserGroup(dto, usersMap))
    return {
        ...res,
        items: userGroups
    }
}

export async function fetchUserGroupById(id: string): Promise<ErrorMessage | UserGroup> {
    const dto = await getUserGroupById(id)

    if (dto && 'errorCode' in dto) {
        return dto
    }

    const userGroup: UserGroup = dtoToUserGroup(dto, {})
    return userGroup
}

export async function createGroup(data: UserGroup): Promise<ErrorMessage | UserGroup> {
    const dto = await createUserGroup(userGroupToDto(data))

    if (dto && 'errorCode' in dto) {
        return dto
    }

    const res = dtoToUserGroup(dto, {})
    return res
}

export async function updateGroup(data: UserGroup): Promise<ErrorMessage | UserGroup> {
    const dto = await updateUserGroup(userGroupToDto(data))

    if (dto && 'errorCode' in dto) {
        return dto
    }

    const res = dtoToUserGroup(dto, {})
    return res
}

export async function deleteGroup(id: string): Promise<ErrorMessage | void> {
    return await deleteUserGroup(id)
}

export async function fetchUserGroupNotifications(id: string): Promise<Record<string, UserGroupNotification>> {
    const dto = await getUserGroupNotifications(id)

    const resourcesStore = useResourcesStore()
    const resourceTypesMap: Record<string, ResourceType> = resourcesStore.types.reduce((map, rt) => {
        map[rt.id] = rt
        return map
    }, {} as Record<string, ResourceType>)

    const res: Record<string, UserGroupNotification> = {}
    for (const key in dto) {
        res[key] = dtoToUserGroupNotification(dto[key], resourceTypesMap)
    }
    return res
}

export async function changeUserGroupNotifications(id: string, data: UserGroupNotification[]): Promise<ErrorMessage | void> {
    const dto = data.map(userGroupNotificationToDto)
    return await saveUserGroupNotifications(id, dto)
}


function dtoToUserGroup(dto: UserGroupDTO, usersMap: Record<string, User>): UserGroup {
    const users: User[] = dto.users.map(userId => usersMap[userId]).filter(u => u !== undefined)

    return {
        id: dto.id,
        name: dto.name,
        description: dto.description,
        users: users,
        enabled: dto.enabled
    }
}

function userGroupToDto(userGroup: UserGroup): UserGroupDTO {
    return {
        id: userGroup.id,
        name: userGroup.name,
        description: userGroup.description,
        users: userGroup.users.map(u => u.id),
        enabled: userGroup.enabled
    }
}

function dtoToUserGroupNotification(dto: UserGroupNotificationDTO, resourceTypeMap: Record<string, ResourceType>): UserGroupNotification {
    const resourceType = resourceTypeMap[dto.resourceTypeId];

    return {
        resourceType: resourceType,
        notifyOnCreate: dto.notifyOnCreation,
        notifyOnUpdate: dto.notifyOnUpdate,
        notifyOnDelete: dto.notifyOnDeletion,
        notifyOnUnconfirmed: dto.notifyOnUnconfirmed,
        enabled: dto.enabled
    }
}

function userGroupNotificationToDto(notification: UserGroupNotification): UserGroupNotificationDTO {
    return {
        resourceTypeId: notification.resourceType.id,
        notifyOnCreation: notification.notifyOnCreate,
        notifyOnUpdate: notification.notifyOnUpdate,
        notifyOnDeletion: notification.notifyOnDelete,
        notifyOnUnconfirmed: notification.notifyOnUnconfirmed,
        enabled: notification.enabled
    }
}